import { useMemo } from "react";

import { useListTranscriptItemsForScribingSession } from "@elation/api-client/src/stitch-web";

import { TranscriptItemEntity } from "@src/features/transcription";
import { STRING_ID_NOT_READY } from "@src/shared/utils/constants";

import type { VisitTranscriptItemV2 } from "./VisitTranscript";

const TRANSCRIPT_POLL_INTERVAL_MS = 3_000;

export interface UseVisitTranscriptItemsOptions {
  encounterId?: number;
  scribingSessionId?: string | null;
}

export interface UseVisitTranscriptItemsResult {
  items: VisitTranscriptItemV2[];
}

/**
 * Polls the live transcript regardless of whether the transcript panel is
 * currently shown. Callers that surface a live line count next to a
 * collapsed toggle (the agenda dock strip) need this hook mounted
 * continuously, not just while the transcript content is visible.
 */
export function useVisitTranscriptItems({
  encounterId,
  scribingSessionId,
}: UseVisitTranscriptItemsOptions): UseVisitTranscriptItemsResult {
  const transcriptItems = useListTranscriptItemsForScribingSession(
    encounterId ?? 0,
    scribingSessionId ?? STRING_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null && scribingSessionId != null,
        refetchInterval: TRANSCRIPT_POLL_INTERVAL_MS,
      },
    },
  );

  const items = useMemo<VisitTranscriptItemV2[]>(() => {
    const sorted = [...(transcriptItems.data?.data ?? [])].sort(
      TranscriptItemEntity.compareByOffsetAsc,
    );
    const speakerLabelMap =
      TranscriptItemEntity.buildSessionSpeakerLabelMap(sorted);

    return sorted.map((item) => ({
      id: String(item.id),
      startOffsetMs: item.startOffsetMs,
      endOffsetMs: item.endOffsetMs,
      speaker: item.speaker,
      speakerLabel: TranscriptItemEntity.sessionSpeakerLabel(
        item.speaker,
        speakerLabelMap,
      ),
      state: item.state,
      text: item.text,
    }));
  }, [transcriptItems.data?.data]);

  return { items };
}
